import type { ContextUpdate, TypedEvents } from "./events";

/** One context type's latest value: the entity id plus whatever extras the host attached. */
export interface ContextEntry {
  id: string;
  metadata: Record<string, string>;
}

/**
 * A per-type view of the cache. Shaped for React's `useSyncExternalStore` — `get` and `subscribe`
 * are stable for the channel's lifetime, so consumers can subscribe without re-registering.
 *
 * The `this: void` annotations are part of the contract: these are implemented as arrow functions
 * closing over the channel's state, so callers may detach them (`const { get } = channel`, or
 * passing `channel.get` straight to `useSyncExternalStore`) with no binding ceremony.
 */
export interface ContextChannel {
  /** Latest entity id for this context type, or undefined if never pushed or seeded. */
  get(this: void): string | undefined;
  getEntry(this: void): ContextEntry | undefined;
  subscribe(this: void, listener: () => void): () => void;
}

export interface HostContext {
  /** Per-type view. Memoized — repeated calls for the same type return the identical object. */
  channel(type: string): ContextChannel;
  /** Seeding and test escape hatch; host pushes arrive via the `context.update` subscription. */
  set(type: string, id: string | undefined, metadata?: Record<string, string>): void;
  /** Detach from the bridge. */
  dispose(): void;
}

export interface HostContextOptions {
  /** Initial ids keyed by context type, e.g. `{ theme: "dark" }` from a URL param. */
  initial?: Record<string, string>;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Last-value cache over the host's `context.update` events, keyed by context type.
 *
 * The bridge itself dispatches events synchronously to whoever is subscribed at that instant and
 * keeps no history, so a late subscriber learns nothing until the next host push. This closes that
 * gap once for every consumer instead of each one hand-rolling a holder.
 *
 * Deliberately domain-agnostic: it stores raw strings and knows nothing about what a "theme" or a
 * "conversation" is. Semantic validation belongs with the feature that cares.
 */
export function createHostContext(
  events: Pick<TypedEvents, "onContextUpdate">,
  opts: HostContextOptions = {},
): HostContext {
  const entries = new Map<string, ContextEntry>();
  const listeners = new Map<string, Set<() => void>>();
  const channels = new Map<string, ContextChannel>();

  for (const [type, id] of Object.entries(opts.initial ?? {})) {
    if (isNonEmptyString(id)) entries.set(type, { id, metadata: {} });
  }

  function set(type: string, id: string | undefined, metadata: Record<string, string> = {}): void {
    const previous = entries.get(type);
    if (id === undefined) {
      if (!previous) return;
      entries.delete(type);
    } else {
      entries.set(type, { id, metadata });
      // Notify on id changes only. Metadata is carried but not reactive — nothing subscribes to it
      // yet, and adding deep comparison before there is a consumer buys nothing.
      if (previous?.id === id) return;
    }
    listeners.get(type)?.forEach((listener) => listener());
  }

  const off = events.onContextUpdate((ctx: ContextUpdate) => {
    // `onContextUpdate` hands over a bare cast of the host payload, so validate before caching.
    if (!ctx || !isNonEmptyString(ctx.type) || !isNonEmptyString(ctx.id)) {
      console.error("[bridge] bad_payload: unusable context.update from host", { ctx });
      return;
    }
    set(ctx.type, ctx.id, ctx.metadata ?? {});
  });

  return {
    channel(type) {
      const existing = channels.get(type);
      if (existing) return existing;

      const channel: ContextChannel = {
        get: () => entries.get(type)?.id,
        getEntry: () => entries.get(type),
        subscribe: (listener) => {
          const bucket = listeners.get(type) ?? new Set<() => void>();
          bucket.add(listener);
          listeners.set(type, bucket);
          return () => {
            bucket.delete(listener);
          };
        },
      };
      channels.set(type, channel);
      return channel;
    },
    set,
    dispose: off,
  };
}
